<template>
  <section class="text-conference-ink-strong border-b border-[#d4d4d4] bg-[#fafafa] last:border-b-0">
    <div class="flex items-start gap-1 px-1.5 py-1">
      <CIconButton
        class="mt-px h-5 w-5 shrink-0 text-[10px]"
        size="sm"
        :tone="expanderTone"
        :aria-expanded="isExpanded"
        :aria-controls="participantListId"
        @click="isExpanded = !isExpanded"
      >
        <span aria-hidden="true">{{ isExpanded ? '−' : '+' }}</span>
        <span class="sr-only">{{ isExpanded ? 'Collapse' : 'Expand' }} {{ room.displayName }}</span>
      </CIconButton>

      <div class="min-w-0 flex-1">
        <div
          class="rounded-[1px] border transition-[background-color,border-color,box-shadow] duration-150"
          :class="
            isDraggedOver && canDropParticipants
              ? 'border-[#8f7b29] bg-[#f5edc8] shadow-[inset_0_0_0_1px_rgba(143,123,41,0.2)]'
              : room.isActive
                ? 'bg-conference-room border-[#d8cf8c]'
                : room.isOpen
                  ? 'border-[#d7d7d7] bg-white'
                  : 'border-[#dbdbdb] bg-[#f4f4f4]'
          "
          @contextmenu.prevent.stop="emit('open-room-menu', room.slug, $event)"
          @dragenter.prevent="handleDragEnter"
          @dragover.prevent="handleDragOver"
          @dragleave.prevent="handleDragLeave"
          @drop.prevent="handleDrop"
        >
          <div class="flex items-center gap-1 px-1.5 py-0.75">
            <button
              :data-testid="`room-switch-${room.slug}`"
              type="button"
              class="focus-visible:ring-conference-accent/35 flex min-w-0 flex-1 items-center gap-1.5 text-left transition-[background-color,color,box-shadow] duration-150 focus-visible:ring-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-60"
              :class="roomButtonClass"
              :aria-label="`${room.displayName}, ${occupancyPeopleLabel}`"
              :disabled="!canSwitchToRoom"
              :aria-current="room.isActive ? 'page' : undefined"
              @dblclick="handleRoomJoin"
              @keydown.enter.prevent="handleRoomJoin"
              @keydown.space.prevent="handleRoomJoin"
            >
              <span class="truncate text-[11px] leading-none font-semibold">
                {{ room.displayName }}
              </span>
              <span
                class="ml-auto shrink-0 text-[10px] font-semibold"
                :class="room.isActive ? 'text-[#6d5c19]' : 'text-[#707070]'"
              >
                {{ occupancyLabel }}
              </span>
              <span class="sr-only">{{ occupancyPeopleLabel }}</span>
            </button>

            <CIconButton
              v-if="showCloseButton"
              size="sm"
              tone="neutral"
              class="h-5 w-5 shrink-0 text-[11px]"
              :data-testid="`close-breakout-room-${room.slug}`"
              :aria-label="`Close ${room.displayName}`"
              :title="`Close ${room.displayName}`"
              @click.stop="emit('close-room', room.slug)"
            >
              <span aria-hidden="true">×</span>
            </CIconButton>
          </div>

          <div
            :id="participantListId"
            :data-testid="`room-participants-${room.slug}`"
            class="grid grid-cols-[max-content_minmax(0,1fr)_max-content_max-content] items-center gap-x-1 gap-y-px px-1.5 pb-1"
            :class="room.isActive ? 'bg-conference-room' : 'bg-transparent'"
            v-show="isExpanded"
          >
            <p v-if="visibleParticipantCount === 0" class="col-span-full px-0.5 py-0.5 text-[10px] text-[#6a6a6a]">
              No one is in this room right now.
            </p>

            <template
              v-for="item in rosterItems"
              :key="item.kind === 'participant' ? item.participant.id : `room-role-divider-${room.slug}`"
            >
              <ConferenceParticipantRow
                v-if="item.kind === 'participant'"
                :chat-visible="chatVisible"
                compact
                :is-confetti-highlighted="confettiHighlightedParticipantIds.includes(item.participant.id)"
                :feedback-visibility="feedbackVisibility"
                :hand-index="handIndexByParticipantId.get(item.participant.id)"
                :draggable="
                  viewerIsTeacher && (item.participant.role !== 'teacher' || item.participant.id === viewerParticipantId)
                    ? true
                    : canNavigate && item.participant.role === 'student' && item.participant.id === viewerParticipantId
                "
                :is-dragged="draggedParticipantIds.includes(item.participant.id)"
                :is-drawing="item.participant.isDrawing"
                :is-highlighted="highlightedParticipantIds.includes(item.participant.id)"
                :is-speaking="speakingParticipantIds.includes(item.participant.id)"
                :is-selected="selectedParticipantIds.includes(item.participant.id)"
                :participant="item.participant"
                :show-participation-count="viewerIsTeacher"
                :viewer-cloaked="viewerCloaked"
                @adjust-participation="emit('adjust-participation', room.slug, item.participant.id, $event)"
                @contextmenu="emit('open-participant-menu', room.slug, item.participant.id, $event)"
                @dragend="emit('participant-dragend')"
                @dragstart="emit('participant-dragstart', room.slug, item.participant.id, $event)"
                @select="emit('select-participant', room.slug, item.participant.id, $event)"
              />
              <div
                v-else
                :data-testid="`room-role-divider-${room.slug}`"
                aria-hidden="true"
                class="col-span-full my-0.5 border-t border-[#d7d7d7]"
              />
            </template>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type {
  ConferenceParticipant,
  ConferenceRoom,
  ConferenceRoomActivity,
  ConferenceRosterParticipant,
} from '../convex/useConference';
import ConferenceParticipantRow from './ConferenceParticipantRow.vue';
import CIconButton from './primitives/CIconButton.vue';

type RosterItem =
  | {
      kind: 'divider';
    }
  | {
      kind: 'participant';
      participant: ConferenceRosterParticipant;
    };

const props = defineProps<{
  canNavigate: boolean;
  chatVisible: boolean;
  activity?: ConferenceRoomActivity | null;
  confettiHighlightedParticipantIds: ConferenceParticipant['id'][];
  draggedParticipantIds: ConferenceParticipant['id'][];
  dragSourceRoomSlugs: string[];
  feedbackVisibility: boolean;
  highlightedParticipantIds: ConferenceParticipant['id'][];
  participants: ConferenceRosterParticipant[];
  room: ConferenceRoom;
  selectedParticipantIds: ConferenceParticipant['id'][];
  speakingParticipantIds: ConferenceParticipant['id'][];
  switchingRoom: boolean;
  viewerCloaked: boolean;
  viewerParticipantId: ConferenceParticipant['id'];
  viewerIsTeacher: boolean;
}>();

const emit = defineEmits<{
  (event: 'adjust-participation', roomSlug: string, participantId: ConferenceParticipant['id'], delta: number): void;
  (event: 'open-participant-menu', roomSlug: string, participantId: ConferenceParticipant['id'], mouseEvent: MouseEvent): void;
  (event: 'open-room-menu', roomSlug: string, mouseEvent: MouseEvent): void;
  (event: 'participant-dragend'): void;
  (event: 'participant-dragstart', roomSlug: string, participantId: ConferenceParticipant['id'], dragEvent: DragEvent): void;
  (event: 'close-room', roomSlug: string): void;
  (event: 'select-room', roomSlug: string): void;
  (event: 'select-participant', roomSlug: string, participantId: ConferenceParticipant['id'], mouseEvent: MouseEvent): void;
  (event: 'drop-participants', roomSlug: string): void;
}>();

const isExpanded = ref(props.room.isActive || props.room.occupancyCount > 0);
const isDraggedOver = ref(false);

watch(
  () => props.room.isActive,
  (isActive) => {
    if (isActive) {
      isExpanded.value = true;
    }
  },
);

watch(
  () => props.room.occupancyCount,
  (occupancyCount) => {
    if (occupancyCount > 0) {
      isExpanded.value = true;
    }
  },
);

const participantListId = computed(() => `room-participants-panel-${props.room.slug}`);
const roomParticipants = computed<ConferenceRosterParticipant[]>(() => {
  const activity = props.activity ?? null;
  const typingParticipantIds = new Set(activity?.typingParticipantIds ?? []);
  const drawingParticipantIds = new Set(activity?.drawingParticipantIds ?? []);

  return props.participants.map((participant) => ({
    ...participant,
    isDrawing: drawingParticipantIds.has(participant.id),
    isTyping: typingParticipantIds.has(participant.id),
  }));
});
const visibleParticipants = computed(() =>
  roomParticipants.value.filter((participant) => participant.role === 'teacher' || participant.role === 'student'),
);
const teacherParticipants = computed(() => visibleParticipants.value.filter((participant) => participant.role === 'teacher'));
const studentParticipants = computed(() => visibleParticipants.value.filter((participant) => participant.role === 'student'));
const visibleParticipantCount = computed(() => visibleParticipants.value.length);
const rosterItems = computed<RosterItem[]>(() => {
  const items: RosterItem[] = teacherParticipants.value.map((participant) => ({
    kind: 'participant',
    participant,
  }));

  if (teacherParticipants.value.length > 0 && studentParticipants.value.length > 0) {
    items.push({ kind: 'divider' });
  }

  items.push(
    ...studentParticipants.value.map((participant) => ({
      kind: 'participant' as const,
      participant,
    })),
  );

  return items;
});
const handIndexByParticipantId = computed(() => {
  const indices = new Map<ConferenceParticipant['id'], number>();
  const visibleParticipantOrderById = new Map(
    visibleParticipants.value.map((participant, index) => [participant.id, index] as const),
  );
  let currentIndex = 1;

  const raisedParticipants = [...visibleParticipants.value]
    .filter((participant) => participant.hand)
    .sort((left, right) => {
      if (left.handRaisedAt < right.handRaisedAt) {
        return -1;
      }

      if (left.handRaisedAt > right.handRaisedAt) {
        return 1;
      }

      return (visibleParticipantOrderById.get(left.id) ?? 0) - (visibleParticipantOrderById.get(right.id) ?? 0);
    });

  for (const participant of raisedParticipants) {
    indices.set(participant.id, currentIndex);
    currentIndex += 1;
  }

  return indices;
});
const occupancyLabel = computed(() => `(${props.room.occupancyCount})`);
const occupancyPeopleLabel = computed(() => {
  return props.room.occupancyCount === 1 ? '1 person' : `${props.room.occupancyCount} people`;
});
const isDragActive = computed(() => props.draggedParticipantIds.length > 0);
const isViewerDraggingSelf = computed(
  () => props.draggedParticipantIds.length === 1 && props.draggedParticipantIds[0] === props.viewerParticipantId,
);
const canDropParticipants = computed(() => {
  if (!props.room.isOpen || props.switchingRoom || !isDragActive.value) {
    return false;
  }

  if (!props.viewerIsTeacher && !(props.canNavigate && isViewerDraggingSelf.value)) {
    return false;
  }

  return !(props.dragSourceRoomSlugs.length === 1 && props.dragSourceRoomSlugs[0] === props.room.slug);
});
const canSwitchToRoom = computed(() => props.room.isOpen && !props.room.isActive && !props.switchingRoom && props.canNavigate);
const showCloseButton = computed(() => props.viewerIsTeacher && props.room.kind === 'breakout');
const expanderTone = computed(() => (isExpanded.value ? 'gold' : 'neutral'));
const roomButtonClass = computed(() =>
  props.room.isActive
    ? 'border-transparent bg-transparent text-[#342d12]'
    : props.room.isOpen
      ? 'rounded-[1px] bg-transparent text-[#2a2a2a] hover:bg-[#f6f1da] hover:text-[#4f4317]'
      : 'bg-transparent text-[#7b7b7b]',
);

function handleRoomJoin(): void {
  if (!canSwitchToRoom.value) {
    return;
  }

  emit('select-room', props.room.slug);
}

function handleDragEnter(): void {
  if (!canDropParticipants.value) {
    return;
  }

  isDraggedOver.value = true;
  isExpanded.value = true;
}

function handleDragOver(event: DragEvent): void {
  if (!canDropParticipants.value) {
    return;
  }

  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = 'move';
  }

  isDraggedOver.value = true;
}

function handleDragLeave(): void {
  isDraggedOver.value = false;
}

function handleDrop(): void {
  if (!canDropParticipants.value) {
    return;
  }

  isDraggedOver.value = false;
  emit('drop-participants', props.room.slug);
}
</script>
